/**
 * CustomerPortal.jsx — public, token-authenticated job view.
 *
 * Mounted at `/portal/:token`, OUTSIDE the auth-guarded route tree (see
 * App.js). The token in the URL is the credential — no login UI here.
 *
 * Scope per Corey (Jun 2026):
 *   • Shows one job + its photos.
 *   • Read-only EXCEPT the customer may upload photos while the job is open.
 *   • Mobile-first — most customers land here from a text/email on a phone.
 *
 * Network contract:
 *   GET  /api/portal/{token}                      → public job view JSON
 *   POST /api/portal/{token}/upload-photo (form)  → 200 {ok, photo}
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2, Clock, AlertTriangle, MapPin, Calendar, Upload, Image as ImageIcon,
  Loader2, ShieldOff, ThumbsUp, ThumbsDown, Truck,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { compressImage, isCompressibleImage } from '@/utils/compressImage';
import { PortalMap } from '@/components/portal/PortalMap';
import { PortalTimeline } from '@/components/portal/PortalTimeline';
import { PortalBilling } from '@/components/portal/PortalBilling';
import { useBrandFont } from '../utils/brandFont';

const API = window.__BACKEND_URL__;

/* Portal photo tile — presigned S3 URLs expire; if one fails to load
   (stale tab, weak signal) show a tap-to-refresh tile that re-pulls the
   job payload (fresh URLs) instead of a silent broken image. */
const PortalPhotoTile = ({ p, i, onStale }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [p.url]);
  if (failed) {
    return (
      <button
        type="button"
        onClick={onStale}
        className="aspect-square rounded-md overflow-hidden border bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center gap-1.5 text-slate-500 text-xs"
        data-testid={`portal-photo-retry-${i}`}
      >
        <ImageIcon className="w-5 h-5" />
        Tap to refresh
      </button>
    );
  }
  return (
    <a
      key={`${p.url}-${i}`}
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block aspect-square rounded-md overflow-hidden border bg-slate-100 dark:bg-slate-800 relative group"
      data-testid={`portal-photo-${i}`}
    >
      <img
        src={p.thumb_url || p.url}
        alt={`Job photo ${i + 1}`}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        onError={() => setFailed(true)}
      />
      {p.label && (
        <span
          className={`absolute top-1.5 right-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded text-white ${p.label === 'before' ? 'bg-sky-600' : 'bg-emerald-600'}`}
          data-testid={`portal-photo-label-${i}`}
        >
          {p.label === 'before' ? 'Before' : 'After'}
        </span>
      )}
      {p.caption && (
        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate" data-testid={`portal-photo-caption-${i}`}>
          {p.caption}
        </span>
      )}
      {p.uploaded_by_role === 'customer' && (
        <Badge
          variant="secondary"
          className="absolute top-1.5 left-1.5 text-[10px] py-0 px-1.5"
        >
          Yours
        </Badge>
      )}
    </a>
  );
};

// ── Status → badge variant + human-readable label ─────────────────────────
const STATUS_META = {
  pending:     { label: 'Pending',      variant: 'secondary', icon: Clock },
  scheduled:   { label: 'Scheduled',    variant: 'secondary', icon: Calendar },
  in_progress: { label: 'In Progress',  variant: 'default',   icon: Clock },
  paused:      { label: 'Paused',       variant: 'destructive', icon: AlertTriangle },
  completed:   { label: 'Completed',    variant: 'default',   icon: CheckCircle2 },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function CustomerPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await axios.get(`${API}/api/portal/${token}`);
      setJob(data);
      if (silent) setError(null);
    } catch (e) {
      // 404 = invalid/revoked token. Anything else = bad-network / transient.
      // Silent background refreshes never blank out a working page.
      const status = e?.response?.status;
      if (!silent) {
        setError(status === 404 ? 'not_found' : 'network');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Live auto-refresh (Jun 2026): poll every 45s so status changes, photos
  // and crew locations appear without the customer reloading the page.
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) load(true);
    }, 45000);
    return () => clearInterval(id);
  }, [load]);

  const handleFile = async (event) => {
    const files = event?.target?.files;
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        let toSend = file;
        if (isCompressibleImage(file) && file.size > 800 * 1024) {
          try { toSend = await compressImage(file); } catch { toSend = file; }
        }
        const fd = new FormData();
        fd.append('file', toSend);
        await axios.post(`${API}/api/portal/${token}/upload-photo`, fd);
      }
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || 'Upload failed — please try again on a stronger connection.',
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Customer satisfaction (👍 / 👎 + optional comment) ─────────────────
  // The widget renders only once `status === 'completed'`. Pre-filling
  // from the loaded job lets the customer SEE their previous answer and
  // edit it — the backend treats POSTs as idempotent.
  const [rating, setRating] = useState(null);          // 'up' | 'down' | null
  const [ratingComment, setRatingComment] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  useEffect(() => {
    const sat = job?.satisfaction;
    if (sat) {
      setRating(sat.rating || null);
      setRatingComment(sat.comment || '');
    }
  }, [job?.satisfaction?.rating, job?.satisfaction?.comment]);

  const submitRating = async (chosen) => {
    // `chosen` may differ from current `rating` if the user toggled. Save
    // the chosen value as the canonical rating. Comment is whatever's
    // currently in the textarea (may be empty).
    setSavingRating(true);
    try {
      await axios.post(`${API}/api/portal/${token}/rating`, {
        rating: chosen,
        comment: ratingComment,
      });
      setRating(chosen);
      toast.success('Thanks for your feedback!');
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || 'Could not save your feedback — please try again.',
      );
    } finally {
      setSavingRating(false);
    }
  };

  // ── Render states ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full" data-testid="portal-error-not-found">
          <CardContent className="py-12 text-center space-y-3">
            <ShieldOff className="w-12 h-12 text-slate-400 mx-auto" />
            <h1 className="text-xl font-semibold">This link isn&apos;t active</h1>
            <p className="text-sm text-slate-500">
              The portal link you opened has been revoked or doesn&apos;t exist. Please
              ask the team who sent it to resend a fresh link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'network' || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="text-xl font-semibold">Couldn&apos;t load this page</h1>
            <p className="text-sm text-slate-500">
              We had trouble reaching the server. Check your connection and try
              again.
            </p>
            <Button onClick={load} data-testid="portal-retry">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = STATUS_META[job.status] || { label: job.status, variant: 'outline', icon: Clock };
  const StatusIcon = meta.icon;
  const addrLine = [job.address?.street_address, job.address?.city, job.address?.state_province, job.address?.postal_code]
    .filter(Boolean).join(', ');
  const photos = job.photos || [];
  const branding = job.branding || {};
  const brandColor = branding.brand_color || '#1E88E5';
  const logoUrl = branding.logo_path ? `${API}${branding.logo_path}` : null;
  const brandFont = useBrandFont(branding.document_branding, branding.company_id);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20" style={{ fontFamily: brandFont }} data-testid="customer-portal">
      <div style={{ height: 4, background: brandColor }} data-testid="portal-brand-bar" />
      <header className="bg-white dark:bg-slate-900 border-b">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={branding.company_name || 'Company logo'}
                className="w-12 h-12 rounded-md object-contain border bg-white flex-shrink-0"
                data-testid="portal-company-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-500 truncate" data-testid="portal-company-name">
                {branding.company_name || 'Your service request'}
              </div>
              <h1 className="text-2xl font-semibold mt-0.5 truncate" data-testid="portal-job-title">
                {job.title || 'Service Job'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="portal-live-indicator">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live
            </span>
            <Badge variant={meta.variant} className="flex items-center gap-1" data-testid="portal-status-badge">
              <StatusIcon className="w-3.5 h-3.5" />
              {meta.label}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Crew en route — live ETA banner */}
        {job.crew_en_route && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20" data-testid="portal-en-route-banner">
            <CardContent className="py-4 flex gap-3 items-start">
              <Truck className="w-5 h-5 text-amber-500 mt-0.5 animate-pulse" />
              <div>
                <div className="font-medium">Your crew is on the way</div>
                <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                  {job.crew_en_route.since ? `Left for your location at ${fmtDate(job.crew_en_route.since)}.` : 'Heading to your location now.'}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pause callout */}
        {job.status === 'paused' && job.pause_reason && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20" data-testid="portal-pause-callout">
            <CardContent className="py-4 flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <div className="font-medium">Work paused</div>
                <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{job.pause_reason}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Job details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {job.description && (
              <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line">
                {job.description}
              </p>
            )}
            {addrLine && (
              <div className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{addrLine}</span>
              </div>
            )}
            {job.scheduled_start && (
              <div className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Scheduled: <strong className="text-slate-800 dark:text-slate-200">{fmtDate(job.scheduled_start)}</strong>
                  {job.scheduled_completion ? ` → ${fmtDate(job.scheduled_completion)}` : ''}
                </span>
              </div>
            )}
            {job.started_at && (
              <div className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Started: <strong className="text-slate-800 dark:text-slate-200">{fmtDate(job.started_at)}</strong></span>
              </div>
            )}
            {job.completed_at && (
              <div className="flex items-start gap-2 text-green-700 dark:text-green-300">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Completed: <strong>{fmtDate(job.completed_at)}</strong></span>
              </div>
            )}
            {job.customer_notes && (
              <div className="mt-3 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 text-blue-900 dark:text-blue-200">
                <div className="text-xs uppercase tracking-wider font-medium mb-1">Note from your team</div>
                <div className="text-sm whitespace-pre-line">{job.customer_notes}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location — job site + PII-free crew arrival pin drops */}
        {(job.site_location || (job.arrival_pins || []).length > 0) && (
          <Card data-testid="portal-location-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <PortalMap siteLocation={job.site_location} arrivalPins={job.arrival_pins || []} enRoutePin={job.crew_en_route} />
              {(job.arrival_pins || []).length > 0 && (
                <p className="text-xs text-slate-500">
                  Green pins show where your crew checked in on arrival.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Estimate → invoice journey + invoice / payment (Sep 2026) */}
        <PortalBilling token={token} brandColor={brandColor} />

        {/* Progress updates — generic, privacy-safe timeline */}
        <Card data-testid="portal-progress-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Progress updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PortalTimeline events={job.timeline || []} />
          </CardContent>
        </Card>

        {/* Satisfaction — appears only once the job is completed.
            One tap to rate; comment is optional. Idempotent on the
            backend so the customer can edit and resubmit any time. */}
        {job.status === 'completed' && (
          <Card data-testid="portal-satisfaction-card">
            <CardHeader>
              <CardTitle className="text-base">How did we do?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-3">
                <Button
                  size="lg"
                  variant={rating === 'up' ? 'default' : 'outline'}
                  onClick={() => submitRating('up')}
                  disabled={savingRating}
                  className={`flex-1 ${rating === 'up' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                  data-testid="portal-rating-up"
                >
                  <ThumbsUp className="w-5 h-5 mr-2" />
                  Great
                </Button>
                <Button
                  size="lg"
                  variant={rating === 'down' ? 'default' : 'outline'}
                  onClick={() => submitRating('down')}
                  disabled={savingRating}
                  className={`flex-1 ${rating === 'down' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}`}
                  data-testid="portal-rating-down"
                >
                  <ThumbsDown className="w-5 h-5 mr-2" />
                  Not great
                </Button>
              </div>
              <textarea
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-sm min-h-[72px]"
                placeholder="Anything else you want to share? (optional)"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value.slice(0, 1000))}
                maxLength={1000}
                data-testid="portal-rating-comment"
              />
              {rating && (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    Thanks — your team will see this feedback. Update any time.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => submitRating(rating)}
                    disabled={savingRating}
                    data-testid="portal-rating-save-comment"
                  >
                    {savingRating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                    Save comment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-slate-500" />
              Photos {photos.length > 0 ? `(${photos.length})` : ''}
            </CardTitle>
            {job.uploads_allowed && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFile}
                  data-testid="portal-upload-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="portal-upload-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading…' : 'Add photo'}
                </Button>
              </>
            )}
          </CardHeader>
          <CardContent>
            {photos.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                {job.uploads_allowed
                  ? 'No photos yet. Tap "Add photo" to send one to your team.'
                  : 'No photos yet.'}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="portal-photo-grid">
                {photos.map((p, i) => (
                  <PortalPhotoTile key={`${p.url}-${i}`} p={p} i={i} onStale={() => load(true)} />
                ))}
              </div>
            )}
            {job.uploads_allowed && (
              <p className="text-xs text-slate-500 mt-3 text-center">
                Photos are auto-compressed to ≤5 MB so they upload quickly on cellular.
              </p>
            )}
          </CardContent>
        </Card>

        <footer className="text-center text-xs text-slate-400 pt-6">
          {job.account_portal_url && (
            <p className="mb-3">
              <a href={job.account_portal_url} className="font-medium underline" style={{ color: brandColor }} data-testid="portal-account-link">View your account, balance and statements</a>
            </p>
          )}
          {branding.company_name ? `${branding.company_name} · ` : ''}Powered by HinesTime · This is your private, secure link.
        </footer>
      </main>
    </div>
  );
}
